import React from 'react';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import SlugRow from './SlugRow';
import AddSlugForm from './AddSlugForm';
import RedirectGuidelines from './RedirectGuidelines';
import {IPresentationMeta} from '../../../../../../incase-domain/presentation';
import {useListSlugs} from '../../../../../../service/hooks/useSlug';
import './slugSettings.scss';
import {isRedirectParam} from '../../../../../../utils/redirect';

interface SlugSettingsProps {
    presentation: IPresentationMeta;
}

function SlugSettings({presentation}: SlugSettingsProps): JSX.Element {
    const {t} = useTranslation();
    const {data: slugs, isLoading} = useListSlugs(presentation._id);
    const requestParams = presentation.requestParams ?? [];
    const requiredParams = requestParams.filter((p) => p.required && !isRedirectParam(p.key));

    return (
        <div className="u-p-m c-slugSettings">
            <div className="c-verticalNavigation__header">
                <div className="c-verticalNavigation__header_title">{t('presEditPage.settings.shortLink.title')}</div>
            </div>

            <p className="u-text-gray-500 u-mb-4">{t('presEditPage.settings.shortLink.description')}</p>

            <RedirectGuidelines requestParams={requestParams} />

            {requiredParams.length > 0 && (
                <div className="c-slugSettings__warning">
                    <Icon icon="solar:danger-triangle-linear" width={16} />
                    <span>
                        {t('presEditPage.settings.shortLink.requiredParamsWarning', {
                            params: requiredParams.map((p) => p.key).join(', '),
                        })}
                    </span>
                </div>
            )}

            <div className="u-flex u-flex-col u-gap-3">
                {isLoading && (
                    <div className="u-flex u-v-center u-gap-2 c-slugSettings__loading">
                        <Icon icon="solar:refresh-linear" width={16} />
                    </div>
                )}

                {!isLoading && slugs?.length === 0 && (
                    <p className="c-slugSettings__empty">{t('presEditPage.settings.shortLink.noSlug')}</p>
                )}

                {slugs?.map((slug) => (
                    <SlugRow
                        key={slug._id}
                        slug={slug}
                        presentationId={presentation._id}
                        requestParams={requestParams}
                        onDeleted={() => {
                            // noop
                        }}
                        onUpdated={() => {
                            // noop
                        }}
                    />
                ))}

                <AddSlugForm
                    presentationId={presentation._id}
                    requestParams={requestParams}
                    onCreated={() => {
                        // noop
                    }}
                />
            </div>
        </div>
    );
}

export default SlugSettings;
